import React, { FC, useState, useEffect, useRef } from 'react'; 
import { Dialog, DialogContent, DialogActions, Button, TextField, Box, IconButton, InputAdornment } from '@mui/material';
import { GoogleMap, Marker, useJsApiLoader, Autocomplete } from '@react-google-maps/api';
import {
  setKey,
  setLanguage,
  setRegion,
  setLocationType,
  fromLatLng
} from 'react-geocode';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import SearchIcon from '@mui/icons-material/Search';
import Heading from '../../Components/Heading/heading.tsx';

setKey("AIzaSyA5izcJ7TGIrfklTCrMx3Q2JmeK_zld1sc");
setLanguage("en");
setRegion("in");
setLocationType("ROOFTOP");

const libraries: ("places")[] = ["places"];

interface MapModalProps {
  open: boolean;
  onClose: () => void;
  onLocationSelect: ( lat: string, lng: string) => void;
  initialLocation?: { lat: number; lng: number };
}

const MapModal: FC<MapModalProps> = ({ open, onClose, onLocationSelect, initialLocation }) => {
  const DEFAULT_LOCATION = { lat: 12.9716, lng: 77.5946 };
  
  const [selectedLocation, setSelectedLocation] = useState<{ lat: number; lng: number } | null>(
    initialLocation || null
  );
    const [address, setAddress] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [currentLocation, setCurrentLocation] = useState<{ lat: number; lng: number } | null>(null);

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: "AIzaSyA5izcJ7TGIrfklTCrMx3Q2JmeK_zld1sc",
    libraries, 
  });

  const mapRef = useRef<google.maps.Map | null>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);

  useEffect(() => {
    if (!open) return;
    const setInitialLocation = async () => {
      if (initialLocation) {
        setSelectedLocation(initialLocation);
        await reverseGeocode(initialLocation.lat, initialLocation.lng);
        return;
      }
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            const { latitude, longitude } = position.coords;
            setCurrentLocation({ lat: latitude, lng: longitude });
            setSelectedLocation({ lat: latitude, lng: longitude });
            await reverseGeocode(latitude, longitude);
          },
          (error) => {
            setSelectedLocation(DEFAULT_LOCATION);
            reverseGeocode(DEFAULT_LOCATION.lat, DEFAULT_LOCATION.lng);
          }
        );
      } else {
        setSelectedLocation(DEFAULT_LOCATION);
        reverseGeocode(DEFAULT_LOCATION.lat, DEFAULT_LOCATION.lng);
      }
    };

    setInitialLocation();
  }, [initialLocation, open]);

  const reverseGeocode = async (lat: number, lng: number) => {
    try {
      setIsLoading(true);
      const response = await fromLatLng(lat.toString(), lng.toString());
      const formattedAddress = response.results[0].formatted_address;
      setAddress(formattedAddress);
    } catch (error) {
      setAddress("Address not found");
    } finally {
      setIsLoading(false);
    }
  };

  const handleMapClick = async (e: google.maps.MapMouseEvent) => {
    if (e.latLng) {
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      setSelectedLocation({ lat, lng });
      await reverseGeocode(lat, lng);
    }
  };

  const onLoadAutocomplete = (autocomplete: google.maps.places.Autocomplete) => {
    autocompleteRef.current = autocomplete;
  };

  // Triggered when user selects a dropdown suggestion
  const onPlaceChanged = () => {
    if (autocompleteRef.current !== null) {
      const place = autocompleteRef.current.getPlace();
      if (place.geometry && place.geometry.location) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        
        setSelectedLocation({ lat, lng });
        
        const newAddress = place.formatted_address || place.name || "";
        setAddress(newAddress);
        setSearchQuery(newAddress); 
        
        mapRef.current?.panTo({ lat, lng });
      } else {
        setAddress("Location found, but coordinates not available");
      }
    }
  };

  // Triggered when user explicitly clicks the search icon
  const handleManualSearch = () => {
    if (!searchQuery.trim() || !mapRef.current) return;

    const service = new google.maps.places.PlacesService(mapRef.current);
    const request: google.maps.places.FindPlaceFromQueryRequest = {
      query: searchQuery,
      fields: ["name", "geometry", "formatted_address"],
    };

    service.findPlaceFromQuery(request, (results, status) => {
      if (status === google.maps.places.PlacesServiceStatus.OK && results && results[0]) {
        const place = results[0];
        
        if (place.geometry && place.geometry.location) {
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();
          setSelectedLocation({ lat, lng });
          
          const newAddress = place.formatted_address || place.name || "";
          setAddress(newAddress);
          setSearchQuery(newAddress);
          
          mapRef.current?.panTo({ lat, lng });
        } else {
          setAddress("Location found, but coordinates not available");
        }
      } else {
        setAddress("Location not found");
      }
    });
  };

  const handleCurrentLocation = () => {
    if (currentLocation) {
      setSelectedLocation(currentLocation);
      reverseGeocode(currentLocation.lat, currentLocation.lng);
      mapRef.current?.panTo(currentLocation);
    } else if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          const loc = { lat: latitude, lng: longitude };
          setCurrentLocation(loc);
          setSelectedLocation(loc);
          await reverseGeocode(latitude, longitude);
          mapRef.current?.panTo(loc);
        },
        (error) => {
          console.error("Error getting current location:", error);
        }
      );
    }
  };

  const handleConfirm = () => {
    if (selectedLocation && address) {
      onLocationSelect(
        selectedLocation.lat.toString(),
        selectedLocation.lng.toString()
      );
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <Box sx={{marginLeft: {xs: '0px',sm: '-180px',},}}>    
        <Heading title="Select Location On Map" closeIcon="true" onClose={onClose} />
      </Box>
      <DialogContent style={{ height: '500px', padding: 0 }}>
        {isLoaded ? (
          <>
            <div style={{ padding: '16px', display: 'flex', gap: '8px', alignItems: 'center' }}>
              <Box sx={{ flex: 1 }}>
                <Autocomplete
                  onLoad={onLoadAutocomplete}
                  onPlaceChanged={onPlaceChanged}
                >
                  <TextField
                    fullWidth
                    label="Search for a place"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => {
                      // Allow Enter key to trigger search if Autocomplete doesn't catch it
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleManualSearch();
                      }
                    }}
                    disabled={isLoading}
                    size="medium"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton 
                            onClick={handleManualSearch}
                            edge="end"
                            color="primary"
                            aria-label="search location"
                          >
                            <SearchIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Autocomplete>
              </Box>
              <IconButton 
                onClick={handleCurrentLocation}
                title="Go to current location"
                color="primary"
              >
                <MyLocationIcon />
              </IconButton>
            </div>

            <GoogleMap
              mapContainerStyle={{ width: '100%', height: '380px' }}
              center={selectedLocation || DEFAULT_LOCATION}
              zoom={15}
              onClick={handleMapClick}
              onLoad={map => (mapRef.current = map)}
            >
              {selectedLocation && <Marker position={selectedLocation} />}
              {currentLocation && (
                <Marker 
                  position={currentLocation} 
                  icon={{
                    path: google.maps.SymbolPath.CIRCLE,
                    scale: 7,
                    fillColor: '#4285F4',
                    fillOpacity: 1,
                    strokeWeight: 2,
                    strokeColor: 'white'
                  }}
                />
              )}
            </GoogleMap>
          </>
        ) : (
          <div style={{ padding: '20px' }}>Loading map...</div>
        )}
        <div style={{ padding: '16px' }}>
          <TextField
            fullWidth
            label="Selected Address"
            value={address}
            InputProps={{
              readOnly: true,
            }}
            helperText={isLoading ? "Loading..." : "Click on the map to select a location or search above"}
          />
        </div>
      </DialogContent>
      <DialogActions style={{ justifyContent: 'center' }}>
        <Button
          onClick={onClose}
          className="custom-cancel-button"
          style={{ marginRight: '10px' }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm} 
          className="custom-button"
          style={{ marginLeft: '10px' }}
        >
          Confirm Location
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MapModal;